import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import confetti from 'canvas-confetti';

import LicenseNavigatorV2 from '~/components/legacy/LicenseNavigatorV2.jsx';
import UsageLicenseTypeSelectItem from '~/components/legacy/UsageLicenseTypeSelectItem.jsx';

const PROJECT_LICENSE_FAMILIES = {
  de: [
    {
      key: 'solo_license',
      title: 'Solo',
      description: 'Für Einzelunternehmer, Freelancer und Creator, die allein arbeiten, auch für kommerzielle Eigen- und Kundenprojekte wie Social Video, Podcast oder Imagefilm.',
      fromPriceLabel: 'ab 14,95 €'
    },
    {
      key: 'business_license',
      title: 'Business',
      description: 'Für Unternehmen, Teams und Agenturen, wenn mehrere Personen oder ein Firmenkontext beteiligt sind, z. B. bei Social Video, Podcast oder Imagefilm ohne Broadcast.',
      fromPriceLabel: 'ab 59,95 €'
    },
    {
      key: 'premium_license',
      title: 'Premium',
      description: 'Für einzelne Projekte mit Musiknutzung in Radio, TV, Kino, VOD oder Trailern.',
      fromPriceLabel: 'ab 249,95 €'
    },
    {
      key: 'campaign_license',
      title: 'Campaign',
      description: 'Für größere Kampagnenrahmen, Produktlaunches und mehrere Assets über mehrere Placements, Motive oder Formate hinweg.',
      fromPriceLabel: 'ab 499,95 €'
    },
    {
      key: 'event_track_license',
      title: 'Event-Track',
      description: 'Für einzelne Titel bei Events, Mehrtagesveranstaltungen und Roadshows mit bis zu 50 Veranstaltungen.',
      fromPriceLabel: 'ab 9,95 €'
    }
  ],
  en: [
    {
      key: 'solo_license',
      title: 'Solo',
      description: 'For solo business owners, freelancers, and creators working on their own, including commercial own-use and client projects such as social videos, podcasts, or brand films.',
      fromPriceLabel: 'from €14.95'
    },
    {
      key: 'business_license',
      title: 'Business',
      description: 'For companies, teams, and agencies whenever multiple people or a company context are involved, for example social videos, podcasts, or brand films without broadcast.',
      fromPriceLabel: 'from €59.95'
    },
    {
      key: 'premium_license',
      title: 'Premium',
      description: 'For single projects using music in radio, TV, cinema, VOD, or trailers.',
      fromPriceLabel: 'from €249.95'
    },
    {
      key: 'campaign_license',
      title: 'Campaign',
      description: 'For larger campaign frameworks, product launches, and multiple assets across several placements, formats, or motifs.',
      fromPriceLabel: 'from €499.95'
    },
    {
      key: 'event_track_license',
      title: 'Event Track',
      description: 'For individual tracks at events, multi-day events, and roadshows with up to 50 events.',
      fromPriceLabel: 'from €9.95'
    }
  ]
};

const LEGACY_SUBSCRIPTION_USAGE_LICENSE_TYPE_PRIORITIES = {
  legacy_premium_international: 70,
  legacy_premium_national: 60,
  legacy_premium_regional: 50,
  legacy_premium: 50,
  premium: 50,
  standard_paid_social: 40,
  legacy_standard: 30,
  standard: 30,
  legacy_mini: 20,
  mini: 20,
  private: 0
};

class UsageLicenseTypeSelect extends Component {

  constructor(props) {
    super(props);

    this.handleClick = this.handleClick.bind(this);
    this.createOverlayPortalRoot = this.createOverlayPortalRoot.bind(this);
    this.destroyOverlayPortalRoot = this.destroyOverlayPortalRoot.bind(this);
    this.renderSelectMarkup = this.renderSelectMarkup.bind(this);
    this.renderOverlayShell = this.renderOverlayShell.bind(this);
    this.handleProjectLicenseFamilySelect = this.handleProjectLicenseFamilySelect.bind(this);
    this.handleSavedProjectLicensePresetSelect = this.handleSavedProjectLicensePresetSelect.bind(this);
    this.handleIncludedLegacySubscriptionUsageLicenseTypeSelect = this.handleIncludedLegacySubscriptionUsageLicenseTypeSelect.bind(this);
    this.handleProjectLicenseFamilyAdd = this.handleProjectLicenseFamilyAdd.bind(this);
    this.handleCurrentProjectLicenseRemove = this.handleCurrentProjectLicenseRemove.bind(this);
    this.handleFamilySelectionReset = this.handleFamilySelectionReset.bind(this);
    this.handleNavigatorOpen = this.handleNavigatorOpen.bind(this);
    this.handleNavigatorBack = this.handleNavigatorBack.bind(this);
    this.handleNavigatorRecommendation = this.handleNavigatorRecommendation.bind(this);
    this.handleDrawerClickCapture = this.handleDrawerClickCapture.bind(this);
    this.handleContinueShopping = this.handleContinueShopping.bind(this);
    this.state = {
      selectedItemId : 0,
      selectedProjectLicenseFamilyKey: null,
      selectedSavedProjectLicensePresetKey: null,
      selectedIncludedLegacySubscriptionUsageLicenseTypeId: null,
      ignoreDefaultFamilySelection: false,
      projectLicensePanelMode: 'family_select',
      projectLicenseSuccessSummary: null
    }
  }

  componentDidMount() {
    this.createOverlayPortalRoot();
  }

  componentDidUpdate(prevProps) {
    if (this.props.position === 'overlay' && !this.overlayPortalRoot) {
      this.createOverlayPortalRoot();
    }

    if (prevProps.position === 'overlay' && this.props.position !== 'overlay') {
      this.destroyOverlayPortalRoot();
    }

    if (this.props.visible && !prevProps.visible) {
      this.setState({
        selectedProjectLicenseFamilyKey: this.defaultProjectLicenseFamilyKey(),
        selectedSavedProjectLicensePresetKey: null,
        selectedIncludedLegacySubscriptionUsageLicenseTypeId: null,
        ignoreDefaultFamilySelection: false,
        projectLicensePanelMode: 'family_select',
        projectLicenseSuccessSummary: null
      });
    }
  }

  componentWillUnmount() {
    this.destroyOverlayPortalRoot();
  }

  createOverlayPortalRoot() {
    if (typeof document === 'undefined' || this.props.position !== 'overlay' || this.overlayPortalRoot) {
      return;
    }

    this.overlayPortalRoot = document.createElement('div');
    this.overlayPortalRoot.className = 'usage-license-type-select__portal';
    document.body.appendChild(this.overlayPortalRoot);

    if (typeof this.forceUpdate === 'function') {
      this.forceUpdate();
    }
  }

  destroyOverlayPortalRoot() {
    if (!this.overlayPortalRoot) {
      return;
    }

    if (this.overlayPortalRoot.parentNode) {
      this.overlayPortalRoot.parentNode.removeChild(this.overlayPortalRoot);
    }

    this.overlayPortalRoot = null;
  }
  handleClick(itemId, forFx) {
      confetti({
        particleCount: 200,
        startVelocity: 30,
        spread: 360
      });

      if (forFx) {
        this.props.updateCart(itemId, undefined, true);
      } else {
        this.props.updateCart(itemId);
      }
  }

  handleProjectLicenseFamilySelect(projectLicenseFamilyKey, e) {
    e.preventDefault();
    if (this.familyBlockedByCart(projectLicenseFamilyKey)) {
      return;
    }

    this.setState({
      selectedProjectLicenseFamilyKey: projectLicenseFamilyKey,
      selectedSavedProjectLicensePresetKey: null,
      selectedIncludedLegacySubscriptionUsageLicenseTypeId: null,
      ignoreDefaultFamilySelection: true,
      projectLicensePanelMode: 'family_select',
      projectLicenseSuccessSummary: null
    });
  }

  handleSavedProjectLicensePresetSelect(savedPresetKey, e) {
    e.preventDefault();

    const savedPreset = this.projectLicensePresets().find((preset) => this.savedPresetKey(preset) === savedPresetKey);
    if (!savedPreset) {
      return;
    }

    this.setState({
      selectedProjectLicenseFamilyKey: savedPreset.familyKey,
      selectedSavedProjectLicensePresetKey: savedPresetKey,
      selectedIncludedLegacySubscriptionUsageLicenseTypeId: null,
      ignoreDefaultFamilySelection: true,
      projectLicensePanelMode: 'family_select',
      projectLicenseSuccessSummary: null
    });
  }

  handleIncludedLegacySubscriptionUsageLicenseTypeSelect(usageLicenseTypeId, e) {
    e.preventDefault();

    this.setState({
      selectedProjectLicenseFamilyKey: null,
      selectedSavedProjectLicensePresetKey: null,
      selectedIncludedLegacySubscriptionUsageLicenseTypeId: usageLicenseTypeId,
      ignoreDefaultFamilySelection: true,
      projectLicensePanelMode: 'family_select',
      projectLicenseSuccessSummary: null
    });
  }

  handleProjectLicenseFamilyAdd(e) {
    e.preventDefault();

    if (this.projectLicenseActionState().disabled) {
      return;
    }

    if (this.state.selectedIncludedLegacySubscriptionUsageLicenseTypeId) {
      if (typeof this.props.updateCart !== 'function') {
        return;
      }

      this.props.updateCart(this.state.selectedIncludedLegacySubscriptionUsageLicenseTypeId);
      this.setState({
        projectLicensePanelMode: 'success',
        projectLicenseSuccessSummary: this.successSummaryForCurrentSelection()
      });
      return;
    }

    if (!this.state.selectedProjectLicenseFamilyKey || typeof this.props.updateProjectLicenseFamily !== 'function') {
      return;
    }

    this.props.updateProjectLicenseFamily(this.selectedProjectLicenseSelectionPayload());
    this.setState({
      projectLicensePanelMode: 'success',
      projectLicenseSuccessSummary: this.successSummaryForCurrentSelection()
    });
  }

  handleCurrentProjectLicenseRemove(e) {
    e.preventDefault();

    if (typeof this.props.removeProjectLicenseSelection !== 'function') {
      return;
    }

    const currentSelection = this.currentProjectLicenseSelection();
    if (!currentSelection) {
      return;
    }

    this.props.removeProjectLicenseSelection(currentSelection);

    this.setState({
      selectedProjectLicenseFamilyKey: null,
      selectedSavedProjectLicensePresetKey: null,
      selectedIncludedLegacySubscriptionUsageLicenseTypeId: null,
      ignoreDefaultFamilySelection: true,
      projectLicensePanelMode: 'family_select',
      projectLicenseSuccessSummary: null
    });
  }

  handleFamilySelectionReset(e) {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }

    this.setState({
      selectedProjectLicenseFamilyKey: null,
      selectedSavedProjectLicensePresetKey: null,
      selectedIncludedLegacySubscriptionUsageLicenseTypeId: null,
      ignoreDefaultFamilySelection: true,
      projectLicensePanelMode: 'family_select',
      projectLicenseSuccessSummary: null
    });
  }

  handleNavigatorOpen(e) {
    e.preventDefault();
    this.setState({ projectLicensePanelMode: 'navigator', projectLicenseSuccessSummary: null });
  }

  handleNavigatorBack(e) {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }

    this.setState({ projectLicensePanelMode: 'family_select', projectLicenseSuccessSummary: null });
  }

  handleNavigatorRecommendation(recommendation) {
    if (!this.projectLicenseFamilyAllowed(recommendation.key)) {
      return;
    }

    this.setState({
      selectedProjectLicenseFamilyKey: recommendation.key,
      selectedSavedProjectLicensePresetKey: null,
      selectedIncludedLegacySubscriptionUsageLicenseTypeId: null,
      projectLicensePanelMode: 'family_select',
      projectLicenseSuccessSummary: null
    });
  }

  handleDrawerClickCapture(event) {
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
  }

  handleContinueShopping(e) {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }

    const closeHandler = this.props.onRequestClose || this.props.handleCartClick;
    if (typeof closeHandler === 'function') {
      closeHandler(e);
    }
  }

  usageLicenseTypeIsSelected(usageLicenseTypeId) {
      let selected = false;

      this.props.selectedUsageLicenseTypeIds.forEach(function(element) {
          if (element === usageLicenseTypeId) {
              selected = true;
          }
      });

      return selected;
  }

  getUsageLicenseTypeCount(usageLicenseTypeId) {
    let count = 0;

    this.props.selectedUsageLicenseTypeIds.forEach(function(element) {
        if (element === usageLicenseTypeId) {
            count++;
        }
    });

    return count;
  }

  defaultProjectLicenseFamilyKey() {
    const selectedFamilyKeys = Array.isArray(this.props.selectedProjectLicenseFamilyKeys) ? this.props.selectedProjectLicenseFamilyKeys : [];
    return selectedFamilyKeys[0] || null;
  }

  resolvedSelectedProjectLicenseFamilyKey() {
    if (this.state.ignoreDefaultFamilySelection) {
      return this.state.selectedProjectLicenseFamilyKey || null;
    }

    return this.state.selectedProjectLicenseFamilyKey || this.defaultProjectLicenseFamilyKey();
  }

  currentProjectLicenseSelection() {
    const selections = Array.isArray(this.props.selectedProjectLicenseSelections) ? this.props.selectedProjectLicenseSelections : [];
    return selections[0] || null;
  }

  currentProjectLicensePresetId() {
    const currentSelection = this.currentProjectLicenseSelection();
    return currentSelection ? (currentSelection.customerProjectLicensePresetId || currentSelection.customerEventLicensePresetId) : null;
  }

  projectLicenseFamilies() {
    const allFamilies = PROJECT_LICENSE_FAMILIES[this.props.locale] || PROJECT_LICENSE_FAMILIES.de;
    const allowedFamilyKeys = this.allowedProjectLicenseFamilyKeys();
    const families = allowedFamilyKeys ? allFamilies.filter((family) => allowedFamilyKeys.includes(family.key)) : allFamilies;
    const cartFamilyKey = this.cartLicenseFamilyKey();
    if (cartFamilyKey === 'event_track') {
      return families.filter((family) => family.key === 'event_track_license');
    }
    if (cartFamilyKey === 'content' && !allowedFamilyKeys?.includes('event_track_license')) {
      return families.filter((family) => family.key !== 'event_track_license');
    }

    return families;
  }

  allowedProjectLicenseFamilyKeys() {
    if (!Array.isArray(this.props.allowedProjectLicenseFamilyKeys) || this.props.allowedProjectLicenseFamilyKeys.length === 0) {
      return null;
    }

    return this.props.allowedProjectLicenseFamilyKeys.map((key) => String(key));
  }

  projectLicenseFamilyAllowed(projectLicenseFamilyKey) {
    const allowedFamilyKeys = this.allowedProjectLicenseFamilyKeys();
    return !allowedFamilyKeys || allowedFamilyKeys.includes(projectLicenseFamilyKey);
  }

  familyBlockedByCart(projectLicenseFamilyKey) {
    const cartFamilyKey = this.cartLicenseFamilyKey();
    if (!cartFamilyKey) {
      return false;
    }

    if (cartFamilyKey === 'event_track') {
      return projectLicenseFamilyKey !== 'event_track_license';
    }

    return projectLicenseFamilyKey === 'event_track_license';
  }

  hasRestrictedProjectLicenseFamilyScope() {
    return Boolean(this.allowedProjectLicenseFamilyKeys());
  }

  cartLicenseFamilyKey() {
    const cartItems = Array.isArray(this.props.cartItems) ? this.props.cartItems : [];
    if (cartItems.some((item) => item.project_license_family_key === 'event_track_license' || item.event_track_license === true)) {
      return 'event_track';
    }
    if (cartItems.some((item) => item.track_id || item.sound_effect_id || item.exposure_pack_id)) {
      return 'content';
    }

    return null;
  }

  savedProjectLicensePresets() {
    if (Array.isArray(this.props.savedProjectLicensePresets)) {
      return this.props.savedProjectLicensePresets;
    }

    if (typeof window !== 'undefined' && Array.isArray(window.__customerProjectLicensePresets)) {
      return window.__customerProjectLicensePresets;
    }

    return [];
  }

  savedEventLicensePresets() {
    if (Array.isArray(this.props.savedEventLicensePresets)) {
      return this.props.savedEventLicensePresets;
    }

    if (typeof window !== 'undefined' && Array.isArray(window.__customerEventLicensePresets)) {
      return window.__customerEventLicensePresets;
    }

    return [];
  }

  savedPresetKey(savedPreset) {
    return `${savedPreset.familyKey}:${savedPreset.id}`;
  }

  includedSubscriptionProjectLicensePresets() {
    if (Array.isArray(this.props.includedSubscriptionProjectLicensePresets)) {
      return this.props.includedSubscriptionProjectLicensePresets;
    }

    if (typeof window !== 'undefined' && Array.isArray(window.__includedSubscriptionProjectLicensePresets)) {
      return window.__includedSubscriptionProjectLicensePresets;
    }

    return [];
  }

  projectLicensePresets() {
    const presets = [
      ...this.savedEventLicensePresets(),
      ...this.includedSubscriptionProjectLicensePresets(),
      ...this.savedProjectLicensePresets()
    ];

    const allowedFamilyKeys = this.allowedProjectLicenseFamilyKeys();
    return presets.filter((preset) => {
      if (allowedFamilyKeys && !allowedFamilyKeys.includes(String(preset.familyKey))) {
        return false;
      }

      return !this.familyBlockedByCart(preset.familyKey);
    });
  }

  includedLegacySubscriptionUsageLicenseTypes() {
    if (this.hasRestrictedProjectLicenseFamilyScope()) {
      return [];
    }

    const sourceTypes = (() => {
      if (Array.isArray(this.props.includedLegacySubscriptionUsageLicenseTypes)) {
        return this.props.includedLegacySubscriptionUsageLicenseTypes;
      }

      if (typeof window !== 'undefined' && Array.isArray(window.__includedLegacySubscriptionUsageLicenseTypes)) {
        return window.__includedLegacySubscriptionUsageLicenseTypes;
      }

      return [];
    })();

    if (sourceTypes.length <= 1) {
      return sourceTypes;
    }

    const highestType = sourceTypes.reduce((highest, usageLicenseType) => {
      if (!highest) {
        return usageLicenseType;
      }

      const highestPriority = LEGACY_SUBSCRIPTION_USAGE_LICENSE_TYPE_PRIORITIES[highest.projectCatalogKey] || 0;
      const currentPriority = LEGACY_SUBSCRIPTION_USAGE_LICENSE_TYPE_PRIORITIES[usageLicenseType.projectCatalogKey] || 0;

      return currentPriority > highestPriority ? usageLicenseType : highest;
    }, null);

    return highestType ? [highestType] : [];
  }

  normalizedOptionKeys(optionKeys) {
    return Array(optionKeys)
      .flatMap((value) => String(value || '').split(','))
      .map((value) => value.trim())
      .filter(Boolean);
  }

  selectedSavedProjectLicensePreset() {
    return this.projectLicensePresets().find((preset) => this.savedPresetKey(preset) === this.state.selectedSavedProjectLicensePresetKey) || null;
  }

  selectedIncludedLegacySubscriptionUsageLicenseType() {
    return this.includedLegacySubscriptionUsageLicenseTypes().find(
      (usageLicenseType) => usageLicenseType.id === this.state.selectedIncludedLegacySubscriptionUsageLicenseTypeId
    ) || null;
  }

  selectedProjectLicenseSelectionPayload() {
    const selectedFamilyKey = this.state.selectedProjectLicenseFamilyKey || this.defaultProjectLicenseFamilyKey();
    if (!selectedFamilyKey || !this.projectLicenseFamilyAllowed(selectedFamilyKey) || this.familyBlockedByCart(selectedFamilyKey)) {
      return null;
    }

    const savedPreset = this.selectedSavedProjectLicensePreset();

    if (savedPreset) {
      const payload = {
        familyKey: savedPreset.familyKey,
        selectedOptionKeys: this.normalizedOptionKeys(savedPreset.selectedOptionKeys),
        projectLicensePresetName: savedPreset.name
      };

      if (!savedPreset.includedInSubscription) {
        if (savedPreset.familyKey === 'event_track_license') {
          payload.customerEventLicensePresetId = savedPreset.id;
        } else {
          payload.customerProjectLicensePresetId = savedPreset.id;
        }
      }

      return payload;
    }

    return { familyKey: selectedFamilyKey };
  }

  successSummaryForCurrentSelection() {
    const localeIsDe = this.props.locale === 'de';
    const selectedIncludedLegacySubscriptionUsageLicenseType = this.selectedIncludedLegacySubscriptionUsageLicenseType();
    if (selectedIncludedLegacySubscriptionUsageLicenseType) {
      return {
        eyebrow: localeIsDe ? 'Abo-Lizenz hinzugefügt' : 'Subscription license added',
        title: selectedIncludedLegacySubscriptionUsageLicenseType.name,
        description: localeIsDe
          ? 'Der Track liegt jetzt mit der gewählten Abo-Lizenz im Warenkorb.'
          : 'The track is now in your cart with the selected subscription license.'
      };
    }

    const savedPreset = this.selectedSavedProjectLicensePreset();
    if (savedPreset) {
      const isEventPreset = savedPreset.familyKey === 'event_track_license';
      return {
        eyebrow: savedPreset.includedInSubscription ?
          (localeIsDe ? 'Abo-Lizenz hinzugefügt' : 'Subscription license added') :
          (isEventPreset ? (localeIsDe ? 'Event-Konfiguration hinzugefügt' : 'Event configuration added') :
            (localeIsDe ? 'Projekt-Lizenz hinzugefügt' : 'Project license added')),
        title: savedPreset.name,
        description: savedPreset.includedInSubscription ?
          (localeIsDe ? 'Der Track liegt jetzt mit der gewählten Abo-Lizenz im Warenkorb.' : 'The track is now in your cart with the selected subscription license.') :
          (isEventPreset ? (localeIsDe ? 'Der Track liegt jetzt mit der gespeicherten Event-Konfiguration im Warenkorb.' : 'The track is now in your cart with the saved event configuration.') :
            (localeIsDe ? 'Die gespeicherte Projekt-Lizenz liegt jetzt im Warenkorb.' : 'The saved project license is now in your cart.'))
      };
    }

    const selectedFamilyKey = this.state.selectedProjectLicenseFamilyKey || this.defaultProjectLicenseFamilyKey();
    const selectedFamily = this.projectLicenseFamilies().find((family) => family.key === selectedFamilyKey);
    return {
      eyebrow: localeIsDe ? 'Lizenzfamilie hinzugefügt' : 'License family added',
      title: selectedFamily ? selectedFamily.title : (localeIsDe ? 'Projekt-Lizenz' : 'Project license'),
      description: localeIsDe
        ? 'Der Track liegt jetzt mit der gewählten Lizenzfamilie im Warenkorb. Sie können direkt zur Kasse gehen oder weiter Tracks sammeln.'
        : 'The track is now in your cart with the selected license family. You can go straight to checkout or continue browsing.'
    };
  }

  formatPresetPrice(cents) {
    return new Intl.NumberFormat(this.props.locale === 'de' ? 'de-DE' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format((cents || 0) / 100);
  }

  savedPresetDescription(savedPreset) {
    const isEventPreset = savedPreset.familyKey === 'event_track_license';
    if (savedPreset.summaryDescription) {
      if (isEventPreset && savedPreset.pricingText) {
        return `${savedPreset.summaryDescription} · ${savedPreset.pricingText}`;
      }

      return savedPreset.summaryDescription;
    }

    const familyTitle = savedPreset.familyTitle || (this.props.locale === 'de' ? 'Projekt-Lizenz' : 'Project license');
    const groupedUpgradeLabels = Array.isArray(savedPreset.groupedUpgradeLabels) ? savedPreset.groupedUpgradeLabels : [];
    const visibleLabels = groupedUpgradeLabels.slice(0, 2);
    const hiddenCount = groupedUpgradeLabels.length - visibleLabels.length;
    const parts = [familyTitle, ...visibleLabels];

    if (hiddenCount > 0) {
      parts.push(`+${hiddenCount}`);
    }

    if (parts.length > 1) {
      return parts.join(' · ');
    }

    if (this.props.locale === 'de') {
      return `${familyTitle} · ${isEventPreset ? 'Gespeicherte Event-Konfiguration' : 'Gespeicherte Projekt-Lizenz'}`;
    }

    return `${familyTitle} · ${isEventPreset ? 'Saved event configuration' : 'Saved project license'}`;
  }

  savedPresetPriceLabel(savedPreset) {
    if (savedPreset.familyKey === 'event_track_license') {
      return this.props.locale === 'de' ? 'Preis wird neu berechnet' : 'Price recalculated at checkout';
    }

    return savedPreset.priceLabel || this.formatPresetPrice(savedPreset.estimatedPriceCents);
  }

  projectLicenseActionState() {
    if (this.state.selectedIncludedLegacySubscriptionUsageLicenseTypeId) {
      return {
        disabled: false,
        label: this.props.locale === 'de' ? 'Diese Abo-Lizenz wählen' : 'Use this subscription license'
      };
    }

    if (!this.state.selectedProjectLicenseFamilyKey) {
      return {
        disabled: true,
        label: this.props.locale === 'de' ? 'Lizenzfamilie wählen' : 'Choose a license family'
      };
    }

    if (!this.projectLicenseFamilyAllowed(this.state.selectedProjectLicenseFamilyKey) || this.familyBlockedByCart(this.state.selectedProjectLicenseFamilyKey)) {
      return {
        disabled: true,
        label: this.props.locale === 'de' ? 'Event-Lizenz im aktuellen Warenkorb nicht möglich' : 'Event license is not available with the current cart'
      };
    }

    if (this.state.selectedSavedProjectLicensePresetKey) {
      const selectedPreset = this.selectedSavedProjectLicensePreset();
      const isEventPreset = selectedPreset && selectedPreset.familyKey === 'event_track_license';

      return {
        disabled: false,
        label: selectedPreset && selectedPreset.includedInSubscription ?
          (this.props.locale === 'de' ? 'Diese Abo-Lizenz wählen' : 'Use this subscription license') :
          (isEventPreset ? (this.props.locale === 'de' ? 'Diese Event-Konfiguration verwenden' : 'Use this event configuration') :
            (this.props.locale === 'de' ? 'Diese Projekt-Lizenz wählen' : 'Use this project license'))
      };
    }

    return {
      disabled: false,
      label: this.props.locale === 'de' ? 'In den Warenkorb legen' : 'Add to cart'
    };
  }

  projectLicenseCheckoutHint() {
    const selectedFamilyKey = this.resolvedSelectedProjectLicenseFamilyKey();
    const savedPreset = this.selectedSavedProjectLicensePreset();
    const selectedIncludedLegacySubscriptionUsageLicenseType = this.selectedIncludedLegacySubscriptionUsageLicenseType();

    if (!selectedFamilyKey || savedPreset || selectedIncludedLegacySubscriptionUsageLicenseType) {
      return null;
    }

    const selectedFamily = this.projectLicenseFamilies().find((family) => family.key === selectedFamilyKey);
    if (!selectedFamily) {
      return null;
    }

    if (selectedFamilyKey === 'event_track_license') {
      return this.props.locale === 'de'
        ? 'Eventdetails wie Besucherzahl, Veranstaltungsanzahl, Laufzeit und Aftermovie konfigurieren Sie im Event-Checkout.'
        : 'Configure audience, number of events, duration, and the aftermovie upgrade in the event checkout.';
    }

    return this.props.locale === 'de'
      ? 'Diese Lizenz können Sie an der Kasse genau auf Ihr Projekt anpassen.'
      : 'You can tailor this license exactly to your project during checkout.';
  }

  projectLicenseSelectionMatchesPreset(selection, preset) {
    if (!selection || !preset) {
      return false;
    }

    const normalizedSelectionOptionKeys = this.normalizedOptionKeys(selection.selectedOptionKeys).sort();
    const normalizedPresetOptionKeys = this.normalizedOptionKeys(preset.selectedOptionKeys).sort();

    return selection.familyKey === preset.familyKey &&
      JSON.stringify(normalizedSelectionOptionKeys) === JSON.stringify(normalizedPresetOptionKeys);
  }

  currentProjectLicensePresetMatch() {
    const currentSelection = this.currentProjectLicenseSelection();
    const currentPresetId = this.currentProjectLicensePresetId();

    return this.projectLicensePresets().find((preset) => {
      if (preset.includedInSubscription) {
        return this.projectLicenseSelectionMatchesPreset(currentSelection, preset);
      }

      return Boolean(currentPresetId) && String(currentPresetId) === String(preset.id);
    }) || null;
  }

  renderProjectLicenseFamilyMarkup() {
    const classes = classNames({
      'usage-license-type-select': true,
      'usage-license-type-select--active': this.props.visible,
      'usage-license-type-select--overlay': this.props.position === 'overlay',
      'usage-license-type-select--cart': this.props.position === 'cart',
      'usage-license-type-select--project-license-family': true
    });
    const closeHandler = this.props.onRequestClose || this.props.handleCartClick;
    const selectedFamilyKey = this.resolvedSelectedProjectLicenseFamilyKey();
    const actionState = this.projectLicenseActionState();
    const navigatorIntroLabel = this.props.locale === 'de' ? 'Unsicher, welche Lizenz passt?' : 'Not sure which license fits?';
    const navigatorActionLabel = this.props.locale === 'de' ? 'Passende Lizenz ermitteln' : 'Find the right license';
    const navigatorBackLabel = this.props.locale === 'de' ? 'Zur Lizenzauswahl zurück' : 'Back to license selection';
    const panelMode = this.state.projectLicensePanelMode || 'family_select';
    const successSummary = this.state.projectLicenseSuccessSummary || this.successSummaryForCurrentSelection();
    const checkoutPath = this.props.checkoutPath || '/c/checkouts/new';
    const dashboardPresetsPath = this.props.dashboardPresetsPath || '/c/project_license_presets';
    const eventDashboardPresetsPath = this.props.eventDashboardPresetsPath || '/c/event_license_presets';
    const includedLegacySubscriptionUsageLicenseTypes = this.includedLegacySubscriptionUsageLicenseTypes();
    const selectedSavedPreset = this.selectedSavedProjectLicensePreset();
    const selectedIncludedLegacySubscriptionUsageLicenseType = this.selectedIncludedLegacySubscriptionUsageLicenseType();
    const currentSelection = this.currentProjectLicenseSelection();
    const cartFamilyKey = this.cartLicenseFamilyKey();
    const cartFamilyHint = cartFamilyKey === 'event_track'
      ? (this.props.locale === 'de'
        ? 'Sie haben bereits eine Event-Track-Lizenz im Warenkorb. Deshalb können Sie aktuell nur weitere Event-Lizenzen hinzufügen.'
        : 'You already have an event track license in your cart. You can currently add only more event licenses.')
      : cartFamilyKey === 'content'
        ? (this.props.locale === 'de'
          ? 'Sie haben bereits eine Content-Lizenz im Warenkorb. Deshalb können Sie aktuell keine Event-Track-Lizenz hinzufügen.'
          : 'You already have a content license in your cart. You cannot add an event track license right now.')
        : null;
    const projectLicenseCheckoutHint = this.projectLicenseCheckoutHint();
    const currentPresetMatch = this.currentProjectLicensePresetMatch();
    const hasCurrentPresetCard = Boolean(currentPresetMatch);
    const highlightedFamilyKey = selectedSavedPreset || selectedIncludedLegacySubscriptionUsageLicenseType ? null : selectedFamilyKey;
    const projectLicensePresetCards = this.projectLicensePresets().map((savedPreset) => {
      const isSubscriptionPreset = savedPreset.includedInSubscription === true;
      const isCartSelectedPreset = isSubscriptionPreset ?
        this.projectLicenseSelectionMatchesPreset(currentSelection, savedPreset) :
        (Boolean(currentPresetMatch) && String(currentPresetMatch.id) === String(savedPreset.id));
      const itemClasses = classNames({
        'usage-license-type-select__family-item': true,
        'usage-license-type-select__family-item--preset': true,
        'usage-license-type-select__family-item--selected': this.savedPresetKey(savedPreset) === this.state.selectedSavedProjectLicensePresetKey,
        'usage-license-type-select__family-item--cart-selected': isCartSelectedPreset
      });

      return (
        <div key={`saved-project-license-preset-${savedPreset.id}`} className='usage-license-type-select__family-card'>
          <a
            href='#'
            className={itemClasses}
            onClick={(event) => this.handleSavedProjectLicensePresetSelect(this.savedPresetKey(savedPreset), event)}
          >
            <div className='usage-license-type-select__family-header'>
              <span className='usage-license-type-select__family-title'>
                {isCartSelectedPreset ? <span className='usage-license-type-select__family-checkmark'>✓</span> : null}
                {savedPreset.name}
              </span>
              <span className='usage-license-type-select__family-price'>
                {this.savedPresetPriceLabel(savedPreset)}
              </span>
            </div>
            <div className='usage-license-type-select__family-description'>{this.savedPresetDescription(savedPreset)}</div>
          </a>
          {isCartSelectedPreset && !isSubscriptionPreset ? (
            <button
              type='button'
              className='usage-license-type-select__selection-remove usage-license-type-select__selection-remove--inline'
              onClick={this.handleCurrentProjectLicenseRemove}
            >
              {this.props.locale === 'de' ? 'Lizenz aus Warenkorb entfernen' : 'Remove license from cart'}
            </button>
          ) : null}
        </div>
      );
    });
    const hasSavedEventLicensePresets = this.savedEventLicensePresets().length > 0;
    const hasSavedProjectLicensePresets = this.includedSubscriptionProjectLicensePresets().length > 0 || this.savedProjectLicensePresets().length > 0;
    const includedLegacySubscriptionCards = includedLegacySubscriptionUsageLicenseTypes.map((usageLicenseType) => {
      const isCartSelectedUsageLicenseType = this.props.selectedUsageLicenseTypeIds.includes(usageLicenseType.id);
      const itemClasses = classNames({
        'usage-license-type-select__family-item': true,
        'usage-license-type-select__family-item--preset': true,
        'usage-license-type-select__family-item--selected': usageLicenseType.id === this.state.selectedIncludedLegacySubscriptionUsageLicenseTypeId,
        'usage-license-type-select__family-item--cart-selected': isCartSelectedUsageLicenseType
      });

      return (
        <div key={`included-legacy-subscription-usage-license-type-${usageLicenseType.id}`} className='usage-license-type-select__family-card'>
          <a
            href='#'
            className={itemClasses}
            onClick={(event) => this.handleIncludedLegacySubscriptionUsageLicenseTypeSelect(usageLicenseType.id, event)}
          >
            <div className='usage-license-type-select__family-header'>
              <span className='usage-license-type-select__family-title'>
                {isCartSelectedUsageLicenseType ? <span className='usage-license-type-select__family-checkmark'>✓</span> : null}
                {usageLicenseType.name}
              </span>
              {usageLicenseType.priceLabel ? (
                <span className='usage-license-type-select__family-price'>{usageLicenseType.priceLabel}</span>
              ) : null}
            </div>
            <div className='usage-license-type-select__family-description'>
              {usageLicenseType.summaryDescription || (this.props.locale === 'de' ? 'In Ihrem Abo inkludiert' : 'Included in your subscription')}
            </div>
          </a>
        </div>
      );
    });
    const visibleFamilyData = this.projectLicenseFamilies().filter((family) => !highlightedFamilyKey || selectedSavedPreset || family.key === highlightedFamilyKey);
    const families = visibleFamilyData.map((family) => {
      const isCartSelectedFamily = currentSelection && family.key === currentSelection.familyKey && !hasCurrentPresetCard;
      const itemClasses = classNames({
        'usage-license-type-select__family-item': true,
        'usage-license-type-select__family-item--selected': family.key === highlightedFamilyKey,
        'usage-license-type-select__family-item--focused': highlightedFamilyKey && family.key === highlightedFamilyKey,
        'usage-license-type-select__family-item--disabled': this.familyBlockedByCart(family.key),
        'usage-license-type-select__family-item--cart-selected': isCartSelectedFamily
      });
      const showRemoveAction = isCartSelectedFamily;

      return (
        <div key={family.key} className='usage-license-type-select__family-card'>
          <a
            href='#'
            className={itemClasses}
            onClick={(event) => this.handleProjectLicenseFamilySelect(family.key, event)}
            aria-disabled={this.familyBlockedByCart(family.key)}
          >
            <div className='usage-license-type-select__family-header'>
              <span className='usage-license-type-select__family-title'>
                {isCartSelectedFamily ? <span className='usage-license-type-select__family-checkmark'>✓</span> : null}
                {family.title}
              </span>
              <span className='usage-license-type-select__family-price'>{family.fromPriceLabel}</span>
            </div>
            <div className='usage-license-type-select__family-description'>{family.description}</div>
          </a>
          {showRemoveAction ? (
            <button
              type='button'
              className='usage-license-type-select__selection-remove usage-license-type-select__selection-remove--inline'
              onClick={this.handleCurrentProjectLicenseRemove}
            >
              {this.props.locale === 'de' ? 'Lizenz aus Warenkorb entfernen' : 'Remove license from cart'}
            </button>
          ) : null}
        </div>
      );
    });

    const hasSelectedProjectLicenseChoice =
      Boolean(selectedFamilyKey) ||
      Boolean(this.state.selectedSavedProjectLicensePresetKey) ||
      Boolean(this.state.selectedIncludedLegacySubscriptionUsageLicenseTypeId) ||
      Boolean(currentSelection) ||
      includedLegacySubscriptionUsageLicenseTypes.some((usageLicenseType) => this.props.selectedUsageLicenseTypeIds.includes(usageLicenseType.id));
    const hasFocusedFamilySelection = hasSelectedProjectLicenseChoice && !selectedSavedPreset;
    const bodyMarkup = panelMode === 'navigator' ? (
      <div className='usage-license-type-select__navigator-shell'>
        <button
          type='button'
          className='usage-license-type-select__navigator-back'
          onClick={this.handleNavigatorBack}
        >
          {navigatorBackLabel}
        </button>
        <LicenseNavigatorV2
          locale={this.props.locale}
          embedded={true}
          showIntro={false}
          showSidebar={false}
          onRecommendationSelect={this.handleNavigatorRecommendation}
          recommendationActionLabel={this.props.locale === 'de' ? 'Empfohlene Familie übernehmen' : 'Use recommended family'}
        />
      </div>
    ) : panelMode === 'success' ? (
      <div className='usage-license-type-select__success-shell'>
        <div className='usage-license-type-select__success-badge'>
          <span className='fa fa-check'></span>
        </div>
        <div className='usage-license-type-select__success-eyebrow'>{successSummary.eyebrow}</div>
        <div className='usage-license-type-select__success-title'>{successSummary.title}</div>
        <div className='usage-license-type-select__success-description'>{successSummary.description}</div>
        <div className='usage-license-type-select__success-actions'>
          <a href={checkoutPath} className='button button--secondary button--full'>
            {this.props.locale === 'de' ? 'Zur Kasse' : 'Checkout'}
          </a>
          <button
            type='button'
            className='button button--ghost-secondary button--full'
            onClick={this.handleContinueShopping}
          >
            {this.props.locale === 'de' ? 'Weiter einkaufen' : 'Continue shopping'}
          </button>
        </div>
        <button
          type='button'
          className='usage-license-type-select__success-back'
          onClick={this.handleFamilySelectionReset}
        >
          {this.props.locale === 'de' ? '← Lizenzwahl anpassen' : '← Adjust license choice'}
        </button>
      </div>
    ) : (
      <>
        {(projectLicensePresetCards.length > 0 || includedLegacySubscriptionCards.length > 0) ? (
          <>
            <div className='usage-license-type-select__family-section-head'>
              <div className='usage-license-type-select__family-section-title'>
                {hasSavedEventLicensePresets && hasSavedProjectLicensePresets
                  ? (this.props.locale === 'de' ? 'Meine gespeicherten Lizenzen' : 'My saved licenses')
                  : hasSavedEventLicensePresets
                    ? (this.props.locale === 'de' ? 'Meine Event-Konfigurationen' : 'My event configurations')
                    : (this.props.locale === 'de' ? 'Meine Projekt-Lizenzen' : 'My project licenses')}
              </div>
              {hasSavedProjectLicensePresets ? (
                <a href={dashboardPresetsPath} className='usage-license-type-select__family-section-link' target='_blank' rel='noreferrer'>
                  {this.props.locale === 'de' ? 'Verwalten' : 'Manage'}
                </a>
              ) : null}
              {hasSavedEventLicensePresets ? (
                <a href={eventDashboardPresetsPath} className='usage-license-type-select__family-section-link' target='_blank' rel='noreferrer'>
                  {this.props.locale === 'de' ? 'Event-Konfigurationen verwalten' : 'Manage event configurations'}
                </a>
              ) : null}
            </div>
            <div className='usage-license-type-select__family-list usage-license-type-select__family-list--presets'>
              {projectLicensePresetCards}
              {includedLegacySubscriptionCards}
            </div>
          </>
        ) : null}
        {!hasSelectedProjectLicenseChoice ? (
          <div className='usage-license-type-select__family-section-title'>
            {this.props.locale === 'de' ? 'Lizenzfamilie auswählen' : 'Select your license family'}
          </div>
        ) : null}
        {hasSelectedProjectLicenseChoice ? (
          <button
            type='button'
            className='usage-license-type-select__selection-reset'
            onClick={this.handleFamilySelectionReset}
          >
            {this.props.locale === 'de' ? '← Andere Lizenzfamilie wählen' : '← Choose another license family'}
          </button>
        ) : null}
        <div className='usage-license-type-select__family-list'>
          {families}
        </div>
        {cartFamilyHint ? (
          <div className='usage-license-type-select__navigator-hint usage-license-type-select__project-license-cta-hint margin-top10'>
            {cartFamilyHint} {this.props.locale === 'de' ? 'Leeren Sie den Warenkorb oder schließen Sie den Einkauf ab, um die andere Lizenzfamilie zu wählen.' : 'Empty the cart or complete the purchase to choose the other license family.'}
          </div>
        ) : null}
        <div className='usage-license-type-select__project-license-footer'>
          {!hasSelectedProjectLicenseChoice && !this.hasRestrictedProjectLicenseFamilyScope() ? (
            <>
              <div className='usage-license-type-select__navigator-hint'>{navigatorIntroLabel}</div>
              <a
                href='#'
                className='button button--ghost-secondary button--full'
                onClick={this.handleNavigatorOpen}
              >
                {navigatorActionLabel}
              </a>
            </>
          ) : null}
          {projectLicenseCheckoutHint ? (
            <div className='usage-license-type-select__navigator-hint usage-license-type-select__project-license-cta-hint'>
              {projectLicenseCheckoutHint}
            </div>
          ) : null}
          <a
            href='#'
            className={classNames('button button--full usage-license-type-select__family-cta', {
              'button--secondary': true,
              'button--disabled': actionState.disabled
            })}
            onClick={this.handleProjectLicenseFamilyAdd}
          >
            {actionState.label}
          </a>
        </div>
      </>
    );

    return (
      <div className={classes} onClick={this.handleDrawerClickCapture}>
        <div className='usage-license-type-select__header usage-license-type-select__header--project-license'>
          <div className='usage-license-type-select__drawer-title'>
            {this.props.locale === 'de' ? 'Lizenzoptionen' : 'License options'}
          </div>
          <div onClick={closeHandler} className='usage-license-type-select__close'><span className='fa fa-times'></span></div>
          {panelMode === 'family_select' ? (
            <div className='usage-license-type-select__track-preview usage-license-type-select__track-preview--compact'>
              {this.props.trackImageMarkup ? (
                <div className='usage-license-type-select__track-image' dangerouslySetInnerHTML={{ __html: this.props.trackImageMarkup }}></div>
              ) : null}
              <div className='usage-license-type-select__track-meta'>
                <div className='usage-license-type-select__track-meta-line'>
                  <div className='usage-license-type-select__track-title'>{this.props.trackTitle}</div>
                  {this.props.trackArtist ? (
                    <>
                      <span className='usage-license-type-select__track-meta-separator'>·</span>
                      <div className='usage-license-type-select__track-artist'>{this.props.trackArtist}</div>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </div>
        {bodyMarkup}
      </div>
    );
  }

  renderSelectMarkup() {
      if (this.props.selectionMode === 'project_license_family') {
        return this.renderProjectLicenseFamilyMarkup();
      }

      let classes = classNames({
            'usage-license-type-select': true,
            'usage-license-type-select--active': this.props.visible,
            'usage-license-type-select--top': this.props.position === 'top',
            'usage-license-type-select--top-showcase': this.props.position === 'top-showcase',
            'usage-license-type-select--showcase': this.props.position === 'showcase',
            'usage-license-type-select--bottom': this.props.position === 'bottom',
            'usage-license-type-select--overlay': this.props.position === 'overlay',
            'usage-license-type-select--cart': this.props.position === 'cart'
          }),
          usageLicenseTypes = this.props.usageLicenseTypes
              .filter(function (usageLicenseType) {
                return usageLicenseType.for_sound_exposure !== true
              })
              .map(function (usageLicenseType, index) {
                return (
                  <UsageLicenseTypeSelectItem
                    locale={this.props.locale}
                    id={usageLicenseType.id}
                    name={usageLicenseType.name}
                    description={usageLicenseType.formatted_description}
                    grossPrice={usageLicenseType.formatted_country_gross_price}
                    discountGrossPrice={usageLicenseType.formatted_country_discount_gross_price}
                    includedInSubscription={this.props.subscribedUsageLicenseTypes.includes(usageLicenseType.id)}
                    selected={this.usageLicenseTypeIsSelected(usageLicenseType.id)}
                    count={this.getUsageLicenseTypeCount(usageLicenseType.id)}
                    handleClick={this.handleClick}
                    free={this.props.free}
                    discount={this.props.discount}
                    i18n={this.props.i18n}
                    forFx={usageLicenseType.for_fx}
                    key={"usage-license-type-" + usageLicenseType.id} />
              );
          }.bind(this)),
          checkoutButton = <span />,
          pricingInfoLink;
      const projectLicensePricingPath = this.props.project_license_pricing_path || (this.props.locale === 'de' ? '/preise/projekte-lizenzen' : '/pricing/project-licenses');

      if (this.props.selectedUsageLicenseTypeIds.length > 0) {
          if (this.props.show_download_button) {
              checkoutButton = (<div className='row'><div className="col12"><a className='button button--secondary button--full usage-license-type-select__checkout-link' href='/c/checkouts/new' title='Download'>Download</a></div></div>);
          } else {
              checkoutButton = (<div className='row'><div className="col12"><a className='button button--secondary button--full usage-license-type-select__checkout-link' href='/c/checkouts/new' title='Download'>{this.props.locale === 'de' ? 'Zur Kasse' : 'Checkout'}</a></div></div>);
          }
      }

      pricingInfoLink = (
        <div className='usage-license-type-select__price-info'>
          <a href={projectLicensePricingPath} className='button button--full' target='_blank'>{this.props.locale === 'de' ? 'Lizenz-Infos' : 'license infos'}</a>
        </div>
      )

      var header;

      const closeHandler = this.props.onRequestClose || this.props.handleCartClick;

      if (this.props.position === 'overlay') {
          header = (<div className='usage-license-type-select__header'>{this.props.locale === 'de' ? 'Wählen Sie Ihre Lizenz(en) für ' + (this.props.trackTitle) + ' aus:' : 'Choose your license(s) for ' + (this.props.trackTitle) + ':'}<div onClick={closeHandler} className='usage-license-type-select__close'><span className='fa fa-times'></span></div></div>)
      }

      return (
        <div className={classes}>
          {header}
          <div className='usage-license-type-select__list-wrapper'>
            {/* <div className='usage-license-type-select__hint'>
              <strong className=''>Bitte wählen Sie eine Lizenz aus:</strong>
            </div> */}
            {usageLicenseTypes}
            <div className='usage-license-type-select__license-assistant-hint'>
              {this.props.locale === 'de' ? (
                <strong className=''>Nicht sicher, welche Lizenz die Richtige ist? Nutzen Sie einfach unseren <a href='/service/lizenz-navigator/start' target='_blank'>Lizenz-Navigator</a>.</strong>
              ):(
                <strong className=''>Not sure which license is right for you? Simply use our <a href='/service/lizenz-navigator/start' target='_blank'>License Navigator</a>.</strong>
              )}
              
            </div>
          </div>
          {checkoutButton}
          <div className='row usage-license-type-select__button-wrapper'>
            <div className='col5'>
              <div onClick={closeHandler} className='button button--ghost button--full margin-top10'>{this.props.locale === 'de' ? 'Schließen' : 'Close'}</div>
            </div>
            <div className='col7'>
              {pricingInfoLink}
            </div>
          </div>

        </div>
      )
  }

  renderOverlayShell(selectMarkup) {
      const closeHandler = this.props.onRequestClose || this.props.handleCartClick;

      return (
        <div className='usage-license-type-select__drawer-shell'>
          <div className='usage-license-type-select__backdrop' onClick={closeHandler}></div>
          {selectMarkup}
        </div>
      );
  }

  render() {
      if (this.props.position === 'overlay' && !this.props.visible) {
        return null;
      }

      const selectMarkup = this.renderSelectMarkup();
      const portalMarkup = this.props.position === 'overlay' ? this.renderOverlayShell(selectMarkup) : selectMarkup;

      if (this.props.position === 'overlay' && this.overlayPortalRoot) {
        return ReactDOM.createPortal(portalMarkup, this.overlayPortalRoot);
      }

      return portalMarkup;
  }
};

UsageLicenseTypeSelect.defaultProps = {
  subscribedUsageLicenseTypes : [],
  selectedProjectLicenseFamilyKeys: [],
  selectedProjectLicenseSelections: [],
  cartItems: [],
  savedProjectLicensePresets: null,
  savedEventLicensePresets: null,
  includedSubscriptionProjectLicensePresets: null,
  customerSignedIn: null,
  dashboardPresetsPath: '/c/project_license_presets',
  eventDashboardPresetsPath: '/c/event_license_presets',
  savePresetPath: null,
  selectionMode: 'legacy'
};

UsageLicenseTypeSelect.propTypes = {
  locale: PropTypes.string
};

export default UsageLicenseTypeSelect;
